import { WorkspaceMemberBase } from "../types/workspace";
import {
    UserWorkspaceRole,
    userWorkspaceRolesInOrder,
} from "../userWorkspaceRoles";
import { InviteCodeBase } from "../types/inviteCode";

/**
 * Minimal entity assignment type for permission validation.
 * Only requires entity.id since permission checks don't need other entity fields.
 */
interface EntityAssignmentForPermissions {
    entity: { id: number };
}

export function validateInviteMember(
    actingMember: WorkspaceMemberBase,
    inviteWorkspaceRole: UserWorkspaceRole,
    inviteEntityAssignments: EntityAssignmentForPermissions[],
) {
    return (
        validateManageWorkspaceMemberRole(actingMember, inviteWorkspaceRole) ??
        validateManageEntityMembership(actingMember, inviteEntityAssignments)
    );
}

export function validateCancelInvite(
    actingMember: WorkspaceMemberBase,
    invite: InviteCodeBase,
) {
    if (!invite.role) {
        return "Not a member invite";
    }

    return (
        validateManageWorkspaceMemberRole(actingMember, invite.role) ??
        validateManageEntityMembership(
            actingMember,
            (invite.entityAssignments ??
                []) as EntityAssignmentForPermissions[],
        )
    );
}

export function validateManageWorkspaceAssignment(
    actingMember: WorkspaceMemberBase,
    managedMember: WorkspaceMemberBase,
) {
    if (actingMember.user.id === managedMember.user.id) {
        return "Cannot update your own assignment";
    }

    return (
        validateManageWorkspaceMemberRole(actingMember, managedMember.role) ??
        validateManageEntityMembership(
            actingMember,
            managedMember.entityAssignments,
        )
    );
}

function validateManageWorkspaceMemberRole(
    actingMember: WorkspaceMemberBase,
    managedMemberRole: UserWorkspaceRole,
): string | null {
    if (
        userWorkspaceRolesInOrder.indexOf(managedMemberRole) <
        userWorkspaceRolesInOrder.indexOf(actingMember.role)
    ) {
        return "Cannot manage members with a higher role";
    }

    return null;
}

function validateManageEntityMembership(
    actingMember: WorkspaceMemberBase,
    entityAssignments: EntityAssignmentForPermissions[],
): string | null {
    // ignore entity requirements if the acting member is a primary admin
    if (actingMember.role === UserWorkspaceRole.PRIMARY_ADMIN) {
        return null;
    }

    const actingUserEntities = new Set(
        actingMember.entityAssignments.map((ea) => ea.entity.id),
    );

    const hasAccessToAllEntities = entityAssignments.every((ea) =>
        actingUserEntities.has(ea.entity.id),
    );

    if (!hasAccessToAllEntities) {
        return "Cannot manage members with access to entities you don't have access to";
    }

    return null;
}
